<template>
	<div
		class="p-4 sm:p-6 md:p-8 bg-neutral border border-accent rounded-xl sm:rounded-2xl lg:rounded-3xl shadow-card hover:shadow-lg transition-shadow duration-300 animate-fadeUp"
	>
		<!-- Header Section -->
		<div class="mb-4 sm:mb-6 text-center">
			<div
				class="inline-flex items-center justify-center w-14 h-14 mb-3 bg-gradient-to-br from-primary/20 to-primary/10 rounded-full sm:w-16 sm:h-16 md:w-20 md:h-20 ring-3 sm:ring-4 ring-primary/20"
			>
				<i class="fas fa-question-circle text-primary"></i>
			</div>
			<h2
				class="mb-2 text-xl font-bold sm:text-2xl md:text-3xl text-slate-900 font-display"
			>
				Help & Support
			</h2>
			<p
				class="max-w-2xl mx-auto text-xs sm:text-sm md:text-base text-slate-600 leading-relaxed"
			>
				Get assistance with your insurance needs from our professional
				intermediaries
			</p>
		</div>

		<div
			class="mb-6 p-4 sm:p-6 bg-primary/20 backdrop-blur-sm rounded-lg sm:rounded-xl border border-primary/30"
		>
			<div class="flex items-center gap-4">
				<img
					src="/assets/Logo/nexus.svg"
					alt="Nexus Company Logo"
					class="w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl object-cover shadow-sm ring-2 ring-white/50"
				/>
				<div class="ml-auto text-right">
					<h2 class="text-lg sm:text-xl font-semibold text-primary">
						Insurance Help Center
					</h2>
					<p class="text-sm sm:text-base text-primary/80">
						Your trusted support for policies and cover details.
					</p>
				</div>
			</div>
		</div>

		<!-- Main Content -->
		<div class="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
			<!-- Left Column -->
			<div class="space-y-4 sm:space-y-6 md:space-y-8">
				<!-- Personal Details Card -->
				<div
					class="bg-white/70 backdrop-blur-sm rounded-xl p-4 sm:p-5 md:p-6 border border-gray-200 shadow-sm"
				>
					<div class="flex items-center mb-3 sm:mb-4">
						<i class="fas fa-user text-primary mr-2"></i>
						<h3
							class="text-base sm:text-lg font-bold text-slate-900 font-display"
						>
							Personal Details
						</h3>
					</div>

					<div class="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
						<div class="sm:col-span-2 md:col-span-1">
							<label
								for="first-name"
								class="flex items-center text-xs sm:text-sm font-semibold text-gray-800 mb-1 sm:mb-2"
							>
								First Name
								<span
									class="text-red-500 ml-1"
									v-if="errors.firstName"
									>*</span
								>
							</label>
							<input
								id="first-name"
								type="text"
								v-model="personalDetails.firstName"
								@blur="validateField('firstName')"
								class="w-full h-10 sm:h-12 px-3 sm:px-4 text-sm sm:text-base text-gray-800 transition-all duration-200 border rounded-lg sm:rounded-xl shadow-sm bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400"
								:class="{ 'border-red-500': errors.firstName }"
								placeholder="Enter your first name"
								aria-describedby="first-name-error"
							/>
							<p
								v-if="errors.firstName"
								id="first-name-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.firstName }}
							</p>
						</div>
						<div class="sm:col-span-2 md:col-span-1">
							<label
								for="last-name"
								class="flex items-center text-xs sm:text-sm font-semibold text-gray-800 mb-1 sm:mb-2"
							>
								Last Name
								<span
									class="text-red-500 ml-1"
									v-if="errors.lastName"
									>*</span
								>
							</label>
							<input
								id="last-name"
								type="text"
								v-model="personalDetails.lastName"
								@blur="validateField('lastName')"
								class="w-full h-10 sm:h-12 px-3 sm:px-4 text-sm sm:text-base text-gray-800 transition-all duration-200 border rounded-lg sm:rounded-xl shadow-sm bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400"
								:class="{ 'border-red-500': errors.lastName }"
								placeholder="Enter your last name"
								aria-describedby="last-name-error"
							/>
							<p
								v-if="errors.lastName"
								id="last-name-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.lastName }}
							</p>
						</div>
						<div class="sm:col-span-2 md:col-span-1">
							<label
								for="email"
								class="flex items-center text-xs sm:text-sm font-semibold text-gray-800 mb-1 sm:mb-2"
							>
								Email Address
								<span
									class="text-red-500 ml-1"
									v-if="errors.email"
									>*</span
								>
							</label>
							<input
								id="email"
								type="email"
								v-model="personalDetails.email"
								@blur="validateField('email')"
								class="w-full h-10 sm:h-12 px-3 sm:px-4 text-sm sm:text-base text-gray-800 transition-all duration-200 border rounded-lg sm:rounded-xl shadow-sm bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400"
								:class="{ 'border-red-500': errors.email }"
								placeholder="Enter your email"
								aria-describedby="email-error"
							/>
							<p
								v-if="errors.email"
								id="email-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.email }}
							</p>
						</div>
						<div class="sm:col-span-2 md:col-span-1">
							<label
								for="kra-pin"
								class="flex items-center text-xs sm:text-sm font-semibold text-gray-800 mb-1 sm:mb-2"
							>
								KRA PIN
								<span
									class="text-red-500 ml-1"
									v-if="errors.kraPin"
									>*</span
								>
							</label>
							<input
								id="kra-pin"
								type="text"
								v-model="personalDetails.kraPin"
								@blur="validateField('kraPin')"
								class="w-full h-10 sm:h-12 px-3 sm:px-4 text-sm sm:text-base text-gray-800 transition-all duration-200 border rounded-lg sm:rounded-xl shadow-sm bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400"
								:class="{ 'border-red-500': errors.kraPin }"
								placeholder="Enter your KRA PIN"
								aria-describedby="kra-pin-error"
							/>
							<p
								v-if="errors.kraPin"
								id="kra-pin-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.kraPin }}
							</p>
						</div>
					</div>
				</div>

				<!-- Vehicle Details Card -->
				<div
					class="bg-white/70 backdrop-blur-sm rounded-xl p-4 sm:p-5 md:p-6 border border-gray-200 shadow-sm"
				>
					<div class="flex items-center mb-3 sm:mb-4">
						<svg
							class="w-4 h-4 sm:w-5 sm:h-5 text-primary mr-2"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4"
							></path>
						</svg>
						<h3
							class="text-base sm:text-lg font-bold text-slate-900 font-display"
						>
							Vehicle Details
						</h3>
					</div>
					<div class="space-y-2 my-3">
						<label
							class="block text-sm font-semibold text-gray-700"
						>
							Select Insurance Class
						</label>

						<div class="relative">
							<!-- Custom caret icon -->
							<i
								class="fas fa-chevron-down text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
							></i>

							<select
								v-model="insurance_class"
								class="w-full h-12 pl-4 pr-10 text-base text-gray-800 rounded-xl border border-gray-300 bg-white/80 backdrop-blur-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400 transition-all duration-200 appearance-none"
							>
								<option disabled value="">Select</option>

								<option
									v-for="cls in insuranceClasses"
									:key="cls._id"
									:value="cls.ira_code"
								>
									{{ cls.name }}
								</option>
							</select>
						</div>
					</div>

					<div class="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
						<div class="sm:col-span-2">
							<label
								for="vehicle-value"
								class="flex items-center text-xs sm:text-sm font-semibold text-gray-800 mb-1 sm:mb-2"
							>
								<i
									class="fas fa-car text-primary mr-1 sm:mr-2"
								></i>
								Vehicle Value (KSH)
								<span
									class="text-red-500 ml-1"
									v-if="errors.vehicleValue"
									>*</span
								>
							</label>
							<div class="relative">
								<span
									class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm sm:text-base font-medium"
									>KSH</span
								>
								<input
									id="vehicle-value"
									type="number"
									v-model="vehicleDetails.vehicleValue"
									@blur="validateField('vehicleValue')"
									class="w-full h-10 sm:h-12 pl-12 sm:pl-16 pr-3 sm:px-4 text-sm sm:text-base text-gray-800 transition-all duration-200 border rounded-lg sm:rounded-xl shadow-sm bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400"
									:class="{
										'border-red-500': errors.vehicleValue,
									}"
									placeholder="e.g., 850000"
									aria-describedby="vehicle-value-error"
								/>
								<p
									v-if="errors.vehicleValue"
									id="vehicle-value-error"
									class="text-xs text-red-500 mt-1"
								>
									{{ errors.vehicleValue }}
								</p>
							</div>
						</div>
						<div class="sm:col-span-2 md:col-span-1">
							<label
								for="reg-no"
								class="flex items-center text-xs sm:text-sm font-semibold text-gray-800 mb-1 sm:mb-2"
							>
								Registration Number
								<span
									class="text-red-500 ml-1"
									v-if="errors.regNo"
									>*</span
								>
							</label>
							<input
								id="reg-no"
								type="text"
								v-model="vehicleDetails.regNo"
								@blur="validateField('regNo')"
								class="w-full h-10 sm:h-12 px-3 sm:px-4 text-sm sm:text-base text-gray-800 transition-all duration-200 border rounded-lg sm:rounded-xl shadow-sm bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400"
								:class="{ 'border-red-500': errors.regNo }"
								placeholder="e.g., KBC 123A"
								aria-describedby="reg-no-error"
							/>
							<p
								v-if="errors.regNo"
								id="reg-no-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.regNo }}
							</p>
						</div>
						<div class="sm:col-span-2 md:col-span-1">
							<label
								for="chassis-no"
								class="flex items-center text-xs sm:text-sm font-semibold text-gray-800 mb-1 sm:mb-2"
							>
								Chassis Number
								<span
									class="text-red-500 ml-1"
									v-if="errors.chassisNo"
									>*</span
								>
							</label>
							<input
								id="chassis-no"
								type="text"
								v-model="vehicleDetails.chassisNo"
								@blur="validateField('chassisNo')"
								class="w-full h-10 sm:h-12 px-3 sm:px-4 text-sm sm:text-base text-gray-800 transition-all duration-200 border rounded-lg sm:rounded-xl shadow-sm bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400"
								:class="{ 'border-red-500': errors.chassisNo }"
								placeholder="Enter full chassis number"
								aria-describedby="chassis-no-error"
							/>
							<p
								v-if="errors.chassisNo"
								id="chassis-no-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.chassisNo }}
							</p>
						</div>
					</div>
				</div>

				<!-- Deadline Card -->
				<div
					class="bg-white/70 backdrop-blur-sm rounded-xl p-4 sm:p-5 md:p-6 border border-gray-200 shadow-sm"
				>
					<div class="flex items-center mb-3 sm:mb-4">
						<svg
							class="w-4 h-4 sm:w-5 sm:h-5 text-primary mr-2"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
							></path>
						</svg>
						<h3
							class="text-base sm:text-lg font-bold text-slate-900 font-display"
						>
							Deadline
						</h3>
					</div>
					<input
						id="expiry"
						type="date"
						v-model="expiry"
						@blur="validateField('expiry')"
						:min="todayDate"
						class="w-full h-10 sm:h-12 px-3 sm:px-4 text-sm sm:text-base text-gray-800 transition-all duration-200 border rounded-lg sm:rounded-xl shadow-sm bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400"
						:class="{ 'border-red-500': errors.expiry }"
						aria-describedby="expiry-error"
					/>
					<p
						v-if="errors.expiry"
						id="expiry-error"
						class="text-xs text-red-500 mt-1"
					>
						{{ errors.expiry }}
					</p>
				</div>
			</div>

			<!-- Right Column -->
			<div class="space-y-4 sm:space-y-6 md:space-y-8">
				<!-- Query Description Card -->
				<div
					class="bg-white/70 backdrop-blur-sm rounded-xl p-4 sm:p-5 md:p-6 border border-gray-200 shadow-sm"
				>
					<div class="flex items-center mb-3 sm:mb-4">
						<i class="fas fa-comments text-primary mr-2"></i>
						<h3
							class="text-base sm:text-lg font-bold text-slate-900 font-display"
						>
							Your Query
						</h3>
					</div>
					<textarea
						v-model="userDescription"
						@blur="validateField('userDescription')"
						class="w-full h-28 sm:h-32 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base text-gray-800 transition-all duration-200 border rounded-lg sm:rounded-xl shadow-sm bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400 resize-none"
						:class="{ 'border-red-500': errors.userDescription }"
						placeholder="Please provide details about your query or issue..."
						aria-describedby="user-description-error"
					></textarea>
					<p
						v-if="errors.userDescription"
						id="user-description-error"
						class="text-xs text-red-500 mt-1"
					>
						{{ errors.userDescription }}
					</p>
					<p class="text-xs text-gray-500 mt-2">
						Describe your insurance needs or any questions you have
					</p>
				</div>

				<!-- Intermediaries Selection Card -->
				<div
					class="bg-white/70 backdrop-blur-sm rounded-xl p-4 sm:p-5 md:p-6 border border-gray-200 shadow-sm"
				>
					<div class="flex items-center justify-between mb-3 sm:mb-4">
						<div class="flex items-center">
							<i class="fas fa-users text-primary mr-2"></i>
							<h3
								class="text-base sm:text-lg font-bold text-slate-900 font-display"
							>
								Select Intermediaries
							</h3>
						</div>
						<div
							class="px-2 sm:px-3 py-1 bg-primary/20 rounded-full"
						>
							<span class="text-xs font-semibold text-primary"
								>{{
									selectedIntermediaries.length
								}}
								selected</span
							>
						</div>
					</div>
					<div class="p-3">
						<input
							type="search"
							v-model="search"
							class="w-full rounded-full p-3 border border-gray-300"
							placeholder="Search email, phone ....."
						/>
					</div>
					<div
						class="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4 max-h-64 sm:max-h-80 overflow-y-auto pr-2 custom-scrollbar intermediary-grid"
					>
						<div
							v-for="intermediary in filteredIntermediaries()"
							v-if="intermediary.status === 'ACTIVE'"
							:key="intermediary.id"
							@click="toggleIntermediary(intermediary.id)"
							:class="[
								'relative group p-3 sm:p-4 bg-white rounded-lg sm:rounded-xl border cursor-pointer transition-all duration-300 transform hover:-translate-y-0.5 sm:hover:-translate-y-1 hover:shadow-md intermediary-card',
								selectedIntermediaries.includes(intermediary.id)
									? 'border-primary shadow-md ring-2 sm:ring-4 ring-primary/20 bg-primary/5'
									: 'border-gray-100 hover:border-primary/50',
							]"
						>
							<div class="card-content">
								<div class="absolute top-2 right-2">
									<div
										:class="[
											'w-4 h-4 sm:w-5 sm:h-5 rounded-full border flex items-center justify-center transition-colors',
											selectedIntermediaries.includes(
												intermediary.id,
											)
												? 'bg-primary border-primary text-white'
												: 'bg-white border-gray-300 group-hover:border-primary/50',
										]"
									>
										<i
											v-if="
												selectedIntermediaries.includes(
													Number(intermediary.id),
												)
											"
											class="fas fa-check w-2.5 h-2.5 sm:w-3 sm:h-3"
										></i>
									</div>
								</div>

								<div class="flex justify-center mb-2 sm:mb-3">
									<div
										class="relative w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden shadow-md border border-white"
									>
										<img
											:src="
												intermediary.profileImage
													? intermediary.profileImage
													: '/assets/Logo/nexus.svg'
											"
											:alt="
												intermediary.intermediaryNames
													? intermediary.intermediaryNames +
														' Logo'
													: 'Default Logo'
											"
											class="w-full h-full object-cover"
										/>
									</div>
								</div>

								<div class="text-center">
									<h4
										class="text-sm font-bold text-slate-900 truncate mb-1"
									>
										{{ intermediary.intermediaryNames }}
									</h4>
									<span
										:class="[
											'inline-block px-2 py-0.5 rounded-full text-xs font-medium',
											intermediary.category === 'broker'
												? 'bg-accent/20 text-primary'
												: 'bg-secondary/20 text-secondary',
										]"
									>
										{{ intermediary.category }}
									</span>
								</div>

								<!-- Per-card View link -->
								<div class="mt-3 text-center card-action">
									<button
										@click.stop="
											openIntermediary(intermediary)
										"
										class="flex items-center justify-center h-8 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm font-semibold text-white rounded-lg sm:rounded-full shadow-md transition-colors duration-200 w-full sm:w-auto bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
									>
										View
									</button>
								</div>
							</div>
						</div>
					</div>
					<p
						v-if="errors.selectedIntermediaries"
						id="intermediaries-error"
						class="text-xs text-red-500 mt-2"
					>
						{{ errors.selectedIntermediaries }}
					</p>
				</div>
			</div>
		</div>

		<!-- Navigation Buttons -->
		<div
			class="flex flex-col mt-6 sm:mt-8 space-y-3 sm:space-y-0 sm:flex-row sm:justify-between sm:items-center sm:space-x-4"
		>
			<button
				@click="goBack"
				class="flex items-center justify-center h-10 sm:h-12 px-4 sm:px-6 text-xs sm:text-sm font-semibold text-secondary border border-secondary rounded-lg sm:rounded-full transition-colors duration-300 w-full sm:w-auto hover:bg-secondary hover:text-white focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2"
			>
				<i class="fas fa-arrow-left mr-1 sm:mr-2"></i>
				Back
			</button>

			<button
				@click="sendRequest"
				:disabled="isFormInvalid"
				class="flex items-center justify-center h-10 sm:h-12 px-4 sm:px-8 text-xs sm:text-sm font-semibold text-white rounded-lg sm:rounded-full shadow-md transition-all duration-300 w-full sm:w-auto bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 transform hover:scale-[1.02] disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
			>
				Send Request
				<i class="fas fa-paper-plane ml-1 sm:ml-2"></i>
			</button>
		</div>
	</div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import {
	FetchInsuranceClasses,
	RequestQuotation,
} from '../../../api/insurance';

export default {
	name: 'MotorPrivateHelp',
	data() {
		return {
			personalDetails: {
				firstName: '',
				lastName: '',
				email: '',
				kraPin: '',
			},
			vehicleDetails: {
				vehicleValue: null,
				regNo: '',
				chassisNo: '',
			},
			expiry: '',
			search: '',
			userDescription: '',
			selectedIntermediaries: [],
			hasAttemptedSubmit: false,
			insurance_class: '',
			insuranceClasses: [],
			errors: {
				firstName: '',
				lastName: '',
				email: '',
				kraPin: '',
				vehicleValue: '',
				regNo: '',
				chassisNo: '',
				expiry: '',
				userDescription: '',
				selectedIntermediaries: '',
			},
		};
	},
	computed: {
		...mapState('insurance/motorPrivate', [
			'selectedCoverType',
			'kycDetails',
			'coverDetails',
		]),
		...mapGetters('help', ['selectEntities']),
		...mapGetters('kyc', ['selectKycData']),
		intermediaries() {
			return this.selectEntities;
		},
	},
	todayDate() {
		const today = new Date();
		const year = today.getFullYear();
		const month = String(today.getMonth() + 1).padStart(2, '0');
		const day = String(today.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	},
	isFormInvalid() {
		// Only check for form validity after submission attempt
		if (!this.hasAttemptedSubmit) return false;

		return (
			Object.values(this.errors).some((error) => error !== '') ||
			Object.values(this.personalDetails).some((val) => !val) ||
			Object.values(this.vehicleDetails).some((val) => !val) ||
			!this.userDescription ||
			this.selectedIntermediaries.length === 0
		);
	},

	methods: {
		...mapActions('help', ['setHelpData']),

		openIntermediary(intermediary) {
			const id =
				intermediary.id ||
				intermediary.intermediaryId ||
				intermediary.intermediarySystemRef;
			if (id)
				this.$router.push({ name: 'ViewIntermediary', params: { id } });
		},

		filteredIntermediaries() {
			const query = this.search ? this.search.toLowerCase().trim() : '';

			if (!query) return this.selectEntities;

			return this.selectEntities.filter((inter) => {
				// check if kyc exists and has at least one entry
				// if (!inter.kyc || inter.kyc.length === 0) {
				//   return false;
				// }

				// const kyc = inter.kyc[0];

				const names = inter.intermediaryNames
					? inter.intermediaryNames.toLowerCase()
					: '';
				const email = inter.primaryEmail
					? inter.primaryEmail.toLowerCase()
					: '';
				// const phone = kyc.contacts ? kyc.contacts.toString() : "";

				return (
					names.includes(query) || email.includes(query)
					// phone.includes(query)
				);
			});
		},
		async fetchClasses() {
			const res = await FetchInsuranceClasses();
			this.insuranceClasses = res.data;
		},

		validateField(field) {
			// Only show real-time errors after user has attempted to submit
			if (!this.hasAttemptedSubmit) return;

			this.errors[field] = '';

			switch (field) {
				case 'firstName':
					if (!this.personalDetails.firstName) {
						this.errors.firstName = 'First name is required';
					} else if (
						!/^[a-zA-Z\s]+$/.test(this.personalDetails.firstName)
					) {
						this.errors.firstName =
							'First name should only contain letters';
					}
					break;
				case 'lastName':
					if (!this.personalDetails.lastName) {
						this.errors.lastName = 'Last name is required';
					} else if (
						!/^[a-zA-Z\s]+$/.test(this.personalDetails.lastName)
					) {
						this.errors.lastName =
							'Last name should only contain letters';
					}
					break;
				case 'email':
					if (!this.personalDetails.email) {
						this.errors.email = 'Email address is required';
					} else if (
						!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
							this.personalDetails.email,
						)
					) {
						this.errors.email =
							'Please enter a valid email address';
					}
					break;
				case 'kraPin':
					if (!this.personalDetails.kraPin) {
						this.errors.kraPin = 'KRA PIN is required';
					} else if (
						!/^[A-Za-z]\d{9}[A-Za-z]$/.test(
							this.personalDetails.kraPin,
						)
					) {
						this.errors.kraPin =
							'Please enter a valid KRA PIN (e.g., A123456789Z)';
					}
					break;
				case 'vehicleValue':
					if (!this.vehicleDetails.vehicleValue) {
						this.errors.vehicleValue = 'Vehicle value is required';
					} else if (this.vehicleDetails.vehicleValue <= 0) {
						this.errors.vehicleValue =
							'Vehicle value must be greater than 0';
					}
					break;
				case 'regNo':
					if (!this.vehicleDetails.regNo) {
						this.errors.regNo = 'Registration number is required';
					} else if (
						!/^[A-Z]{3}\s?\d{3}[A-Z]$/.test(
							this.vehicleDetails.regNo,
						)
					) {
						this.errors.regNo =
							'Please enter a valid registration number (e.g., KBC 123A)';
					}
					break;
				case 'chassisNo':
					if (!this.vehicleDetails.chassisNo) {
						this.errors.chassisNo = 'Chassis number is required';
					} else if (this.vehicleDetails.chassisNo.length < 3) {
						this.errors.chassisNo =
							'Chassis number should be 3 characters long';
					}
					break;
				case 'expiry':
					if (!this.expiry) {
						this.errors.expiry = 'Expiry date is required';
					} else if (
						new Date(this.expiry) < new Date(this.todayDate)
					) {
						this.errors.expiry =
							'Expiry date cannot be in the past';
					}
					break;
				case 'userDescription':
					if (!this.userDescription) {
						this.errors.userDescription =
							'Query description is required';
					} else if (this.userDescription.length < 10) {
						this.errors.userDescription =
							'Description must be at least 10 characters';
					}
					break;
				case 'selectedIntermediaries':
					if (this.selectedIntermediaries.length === 0) {
						this.errors.selectedIntermediaries =
							'Please select at least one intermediary';
					}
					break;
			}
		},

		validateAllFields() {
			const fields = [
				'firstName',
				'lastName',
				'email',
				'kraPin',
				'vehicleValue',
				'regNo',
				'chassisNo',
				'expiry',
				'userDescription',
				'selectedIntermediaries',
			];
			fields.forEach((field) => {
				this.validateField(field);
			});
			return !Object.values(this.errors).some((error) => error !== '');
		},

		goBack() {
			this.$router.push(-1);
		},

		toggleIntermediary(id) {
			// const numId = String(Number(id));
			const index = this.selectedIntermediaries.indexOf(id);
			if (index === -1) {
				this.selectedIntermediaries.push(id);
			} else {
				this.selectedIntermediaries.splice(index, 1);
			}
			// Only validate if user has attempted to submit
			if (this.hasAttemptedSubmit) {
				this.validateField('selectedIntermediaries');
			}
		},

		helpData() {
			return {
				entityRefs: this.selectedIntermediaries,
				insuranceClassSystemRef: this.insurance_class,
				expiry: this.expiry,
				status: 'PENDING',
				remarks: this.userDescription,
				clientDetails: {
					names: {
						firstName: this.personalDetails.firstName || '',
						lastName: this.personalDetails.lastName || '',
					},
					email: this.personalDetails.email,
					kraPin: this.personalDetails.kraPin,
				},
				riskDetails: {
					value: Number(this.vehicleDetails.vehicleValue),
					chassisNumber: this.vehicleDetails.chassisNo,
					regNo: this.vehicleDetails.regNo,
					riskRef: this.vehicleDetails.regNo,
				},
			};
		},

		async sendRequest() {
			// Mark that user has attempted to submit
			this.hasAttemptedSubmit = true;

			// Validate all fields
			if (!this.validateAllFields()) {
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Form Validation Error',
					message:
						'Please correct the highlighted errors before submitting.',
					duration: 4000,
				});
				return;
			}

			// Check for required fields
			const requiredPersonalFields = Object.keys(
				this.personalDetails,
			).filter((field) => !this.personalDetails[field]);
			const requiredVehicleFields = Object.keys(
				this.vehicleDetails,
			).filter((field) => !this.vehicleDetails[field]);

			if (
				requiredPersonalFields.length > 0 ||
				requiredVehicleFields.length > 0 ||
				!this.userDescription ||
				this.selectedIntermediaries.length === 0
			) {
				const missingFields = [];

				if (requiredPersonalFields.length > 0) {
					missingFields.push(
						...requiredPersonalFields.map((field) => {
							switch (field) {
								case 'firstName':
									return 'First Name';
								case 'lastName':
									return 'Last Name';
								case 'kraPin':
									return 'KRA PIN';
								default:
									return (
										field.charAt(0).toUpperCase() +
										field.slice(1)
									);
							}
						}),
					);
				}

				if (requiredVehicleFields.length > 0) {
					missingFields.push(
						...requiredVehicleFields.map((field) => {
							switch (field) {
								case 'vehicleValue':
									return 'Vehicle Value';
								case 'regNo':
									return 'Registration Number';
								case 'chassisNo':
									return 'Chassis Number';
								default:
									return (
										field.charAt(0).toUpperCase() +
										field.slice(1)
									);
							}
						}),
					);
				}

				if (!this.userDescription)
					missingFields.push('Query Description');
				if (this.selectedIntermediaries.length === 0)
					missingFields.push('Intermediary Selection');

				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Incomplete Form',
					message: `Please fill in: ${missingFields.join(', ')}`,
					duration: 4000,
				});
				return;
			}

			this.setHelpData(this.helpData());

			try {
				const res = await RequestQuotation(this.helpData());
				if (res.status) {
					Event.$emit('notifications:show', {
						type: 'success',
						title: 'Request Submitted',
						message:
							'Your quotation request has been successfully submitted.',
						duration: 3000,
					});

					// Reset form
					this.resetForm();
				} else {
					throw new Error('Failed to submit request');
				}
			} catch (err) {
				const errorMessage =
					(err.response &&
						err.response.data &&
						err.response.data.errors) ||
					'Failed to submit request. Please try again.';
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Submission Error',
					message: errorMessage,
					duration: 4000,
				});
			}
		},

		resetForm() {
			this.personalDetails = {
				firstName: '',
				lastName: '',
				email: '',
				kraPin: '',
			};
			this.vehicleDetails = {
				vehicleValue: null,
				regNo: '',
				chassisNo: '',
			};
			this.expiry = '';
			this.userDescription = '';
			this.selectedIntermediaries = [];
			this.hasAttemptedSubmit = false;
			this.errors = {
				firstName: '',
				lastName: '',
				email: '',
				kraPin: '',
				vehicleValue: '',
				regNo: '',
				chassisNo: '',
				expiry: '',
				userDescription: '',
				selectedIntermediaries: '',
			};
		},
	},

	async mounted() {
		if (this.selectKycData && this.selectKycData.names) {
			this.personalDetails = {
				firstName: this.selectKycData.names.firstName || '',
				lastName: this.selectKycData.names.lastName || '',
				email: this.selectKycData.email || '',
				kraPin: this.selectKycData.kraPin || '',
			};
		}
		console.log('entities', this.filteredIntermediaries());
		await this.fetchClasses();
		console.log('===classes', this.insuranceClasses);
	},
};
</script>

<style scoped>
@keyframes fade-in-up {
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}
.animate-fadeUp {
	animation: fade-in-up 0.8s ease-out forwards;
}

input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}
input[type='number'] {
	-moz-appearance: textfield;
}

.font-display {
	font-family:
		'Nunito',
		ui-sans-serif,
		system-ui,
		-apple-system,
		BlinkMacSystemFont,
		'Segoe UI',
		Roboto,
		'Helvetica Neue',
		Arial,
		'Noto Sans',
		sans-serif;
}

.custom-scrollbar::-webkit-scrollbar {
	width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
	background: #f1f5f9;
	border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
	background: #cbd5e1;
	border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
	background: #94a3b8;
}

button:focus,
input:focus,
textarea:focus {
	outline: 2px solid #669bbc;
	outline-offset: 2px;
}

.border-red-500 {
	transition: border-color 0.3s ease;
}

.intermediary-grid {
	box-sizing: border-box;
	min-width: 0;
}

.intermediary-card {
	min-width: 0;
	box-sizing: border-box;
	overflow: hidden;
}

.card-action {
	box-sizing: border-box;
	display: flex;
	justify-content: center;
	align-items: center;
}

.card-action > button {
	display: inline-block;
	margin: 0 auto;
	box-sizing: border-box;
	max-width: 100%;
	width: auto;
}

@media (max-width: 640px) {
	.card-action > button {
		width: 100% !important;
	}

	.intermediary-card .card-action {
		left: 0;
		right: 0;
		padding-left: 0.75rem;
		padding-right: 0.75rem;
		box-sizing: border-box;
	}
}

@media (min-width: 640px) {
	.card-action > button {
		border-radius: 9999px;
	}
}

@media (max-width: 640px) {
	.intermediary-grid {
		grid-auto-rows: auto;
		gap: 0.5rem;
	}

	.intermediary-card {
		position: relative;
		padding: 0 !important;
		box-sizing: border-box;
	}

	.intermediary-card::before {
		content: '';
		display: block;
		padding-top: 100%;
		box-sizing: border-box;
		pointer-events: none;
	}

	.intermediary-card .card-content {
		position: absolute;
		inset: 0;
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
		padding: 0.75rem 0.75rem 3.5rem 0.75rem;
		box-sizing: border-box;
		overflow: hidden;
	}

	.intermediary-card .card-content h4 {
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: normal;
	}

	.intermediary-card .card-action {
		position: absolute;
		left: 0.75rem;
		right: 0.75rem;
		bottom: 0.75rem;
		z-index: 20;
		display: flex;
		justify-content: center;
	}

	.intermediary-card .card-action > button {
		width: 100%;
		max-width: 100%;
	}
}

@media (max-width: 400px) {
	.p-4 {
		padding: 1rem;
	}
	h2 {
		font-size: 1.25rem;
	}
	.text-sm {
		font-size: 0.875rem;
	}
	.text-xs {
		font-size: 0.75rem;
	}
}

@media (max-width: 896px) and (orientation: landscape) {
	.p-4 {
		padding: 0.75rem;
	}
	.mb-4 {
		margin-bottom: 0.75rem;
	}
	.gap-4 {
		gap: 0.75rem;
	}
	.mt-6 {
		margin-top: 1rem;
	}
}

@media (prefers-reduced-motion: reduce) {
	.animate-fadeUp {
		animation: none;
	}
	.transition-all {
		transition: none;
	}
}
</style>
